import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import AppLayout from '../components/AppLayout';
import { 
  FileText, 
  CreditCard, 
  Receipt, 
  FileCheck, 
  Calculator,
  Building2,
  TrendingUp,
  AlertCircle,
  CheckCircle,
  ArrowRight,
  Sparkles,
  BookOpen,
  Shield,
  BarChart3
} from 'lucide-react';

export default function DocsGuidePage() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const primaryDocs = [
    { 
      icon: FileText, 
      name: 'Tax Returns (Form 1065, K-1)', 
      description: 'State and federal tax returns from the last 2-3 years are crucial for verifying income and providing a historical overview of your business\'s financial position.',
      tips: [
        'Schedule K-1 shows partner\'s share of income',
        'Form 1125-A details Cost of Goods Sold',
        'Provides annual revenue baseline'
      ]
    },
    { 
      icon: CreditCard, 
      name: 'Bank Statements', 
      description: 'Bank statements provide an objective look at cash flows and can be used to trace revenues and expenses month-by-month.',
      tips: [
        'Include all business accounts',
        '12-24 months of history recommended',
        'Our AI extracts and categorizes transactions'
      ]
    },
    { 
      icon: Receipt, 
      name: 'Sales & Revenue Reports', 
      description: 'Daily or monthly revenue records, POS reports, and customer orders help demonstrate consistent patterns and income drop during interruption.',
      tips: [
        'POS system exports are ideal',
        'Shows seasonal patterns',
        'Helps establish "but-for" baseline'
      ]
    },
  ];

  const secondaryDocs = [
    { 
      icon: FileCheck, 
      name: 'Payroll Records', 
      description: 'Employee wage logs help support claims for continuing expenses, especially if essential staff remained on payroll.',
    },
    { 
      icon: FileText, 
      name: 'Invoices & Contracts', 
      description: 'Supplier contracts, customer orders, and cancelled contracts prove lost opportunities and ongoing expenses.',
    },
    { 
      icon: Building2, 
      name: 'Inventory Records', 
      description: 'Inventory lists and supply chain documents show business volume before and after interruption.',
    },
    { 
      icon: Calculator, 
      name: 'Budgets & Forecasts', 
      description: 'Pre-loss budgets and financial projections support estimates of expected earnings.',
    },
    { 
      icon: Receipt, 
      name: 'Utility Bills & Expenses', 
      description: 'Utility bills, lease payments, and expense records demonstrate continuing operating costs.',
    },
  ];

  const challenges = [
    {
      title: 'Risk of Under-Valuation',
      description: 'Without standard P&Ls, insurers may argue losses are overstated. Our system addresses this by validating against industry benchmarks.',
      solution: 'BLS-validated industry comparisons'
    },
    {
      title: 'Establishing "But-For" Baseline',
      description: 'Proving what revenue would have been requires historical trend analysis when monthly data is missing.',
      solution: 'Prophet-based forecasting with BLS integration'
    },
    {
      title: 'Identifying Saved vs. Continuing Expenses',
      description: 'Insurers may incorrectly categorize continuing expenses as "saved" to reduce payouts.',
      solution: 'AI-powered expense classification'
    },
    {
      title: 'Extended Investigation Delays',
      description: 'Incomplete records can slow down claims processing significantly.',
      solution: 'Comprehensive document package with automated validation'
    },
  ];

  return (
    <AppLayout>
      <div className="p-6 lg:p-8 max-w-4xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-2">
            <div className="p-2 bg-blue-100 rounded-lg">
              <BookOpen className="w-6 h-6 text-blue-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Documentation Guide</h1>
          </div>
          <p className="text-gray-600">
            Everything you need to know about filing a business interruption claim with or without P&L statements.
          </p>
        </div>

        {/* Key Message */}
        <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-xl p-6 mb-8">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-blue-600 rounded-xl">
              <Sparkles className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-blue-900 mb-2">No P&L? No Problem.</h2>
              <p className="text-blue-800">
                If monthly Profit & Loss statements are unavailable, you can still file a business interruption 
                claim. Our AI-powered system can <strong>reconstruct your P&L</strong> from bank statements, tax returns, 
                and other financial records—validated against industry benchmarks for maximum credibility.
              </p>
            </div>
          </div>
        </div>

        {/* Primary Documents */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <CheckCircle className="w-5 h-5 text-green-600" />
            Primary Alternative Documents
          </h2>
          <p className="text-gray-600 mb-4">
            These documents are most effective for reconstructing your financial position:
          </p>
          
          <div className="space-y-4">
            {primaryDocs.map((doc, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-xl p-5 hover:shadow-md transition-shadow">
                <div className="flex items-start gap-4">
                  <div className="p-3 bg-green-100 rounded-xl">
                    <doc.icon className="w-5 h-5 text-green-600" />
                  </div>
                  <div className="flex-1">
                    <h3 className="font-semibold text-gray-900 mb-1">{doc.name}</h3>
                    <p className="text-gray-600 text-sm mb-3">{doc.description}</p>
                    <div className="flex flex-wrap gap-2">
                      {doc.tips.map((tip, j) => (
                        <span key={j} className="text-xs bg-green-50 text-green-700 px-2 py-1 rounded-full border border-green-200">
                          {tip}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Secondary Documents */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <FileText className="w-5 h-5 text-blue-600" />
            Supporting Documents
          </h2>
          <p className="text-gray-600 mb-4">
            These additional documents strengthen your claim:
          </p>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {secondaryDocs.map((doc, i) => (
              <div key={i} className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <doc.icon className="w-5 h-5 text-gray-500 mt-0.5" />
                  <div>
                    <h4 className="font-medium text-gray-900 text-sm">{doc.name}</h4>
                    <p className="text-xs text-gray-600 mt-1">{doc.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* How Reconstruction Works */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <TrendingUp className="w-5 h-5 text-blue-600" />
            How P&L Reconstruction Works
          </h2>
          
          <div className="bg-white border border-gray-200 rounded-xl p-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-blue-600">1</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Upload</h4>
                <p className="text-xs text-gray-600">Upload bank statements, tax returns, and other records</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-blue-600">2</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Extract</h4>
                <p className="text-xs text-gray-600">AI extracts transactions and financial data automatically</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-blue-600">3</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Categorize</h4>
                <p className="text-xs text-gray-600">Transactions classified to expense categories using AI</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <span className="text-xl font-bold text-blue-600">4</span>
                </div>
                <h4 className="font-semibold text-gray-900 mb-1">Validate</h4>
                <p className="text-xs text-gray-600">Results compared against BLS industry benchmarks</p>
              </div>
            </div>
          </div>
        </section>

        {/* Industry Benchmarks */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5 text-blue-600" />
            Industry Benchmark Validation
          </h2>
          
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-5">
            <div className="flex items-start gap-4">
              <Shield className="w-6 h-6 text-amber-600 flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-amber-900 mb-2">The "Yardstick Method"</h3>
                <p className="text-sm text-amber-800 mb-3">
                  When your business lacks historical P&L data, we use the Yardstick Method—comparing your 
                  reconstructed financials against industry averages from the Bureau of Labor Statistics (BLS).
                </p>
                <ul className="text-sm text-amber-800 space-y-1">
                  <li>• COGS ratios compared to NAICS industry averages</li>
                  <li>• Expense categories validated against sector norms</li>
                  <li>• Profit margins benchmarked for reasonableness</li>
                  <li>• Adds credibility for insurers and adjusters</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        {/* Common Challenges */}
        <section className="mb-10">
          <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
            <AlertCircle className="w-5 h-5 text-red-500" />
            Challenges Without P&Ls (And How We Solve Them)
          </h2>
          
          <div className="space-y-3">
            {challenges.map((challenge, i) => (
              <div key={i} className="bg-white border border-gray-200 rounded-lg p-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-medium text-gray-900">{challenge.title}</h4>
                    <p className="text-sm text-gray-600 mt-1">{challenge.description}</p>
                  </div>
                  <div className="flex-shrink-0">
                    <span className="inline-flex items-center gap-1 text-xs bg-green-100 text-green-700 px-2 py-1 rounded-full">
                      <CheckCircle className="w-3 h-3" />
                      {challenge.solution}
                    </span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* CTA */}
        <div className="bg-gray-900 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold text-white mb-3">Ready to Start Your Claim?</h2>
          <p className="text-gray-300 mb-6 max-w-lg mx-auto">
            Whether you have P&L statements or need to use alternative documentation, 
            our AI-powered system will guide you through the process.
          </p>
          <Link
            to="/new-claim"
            className="inline-flex items-center gap-2 bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-semibold transition-colors"
          >
            Start New Claim
            <ArrowRight className="w-5 h-5" />
          </Link>
        </div>
      </div>
    </AppLayout>
  );
}
